import { axios, getAxiosConfig, ServerAxiosRequestData } from '@config/axios';
import Environment from '@config/index';
import { ApiResponse } from '@interfaces/api';
import {
  ProductDraftWithFormResponse,
  UpdateDraftResponse,
  SubmitDraftResponse,
  CreateProductDraftFromAiParams,
  CreateProductDraftFromAiResponse,
} from '../models/draft-api.types';
import { Universe, Brand } from '../models/category.types';
import {
  PriceDetailsResponse,
  PriceZoneRequestParams,
  PriceZoneResponse,
  RecommendationsPriceResponse,
} from '../models/price.types';
import { DraftFieldUpdates } from '../models/listing-draft.types';
import {
  AddProductDraftPhotosResponse,
  CreateAiProductDraftResponse,
  FetchProductDraftPhotosResponse,
  PhotoReorderItem,
  PhotoUploadResponse,
  PhotoUploadResult,
} from '../models/photo.types';
import { InferAiProductDraftResponse } from '../models/analysis.types';
import { AddressDetails } from '../models/address.types';
import { GenerateDescriptionResponse } from '../models/description.types';

const BASE_URL = Environment.apiBaseUrl;
const SHIPPING_ADDRESS_FLAG = 'shipping';

const FIELD_KEY_MAP: Record<string, string> = {
  model: 'preduct_model',
};

const isNotFoundError = (error: unknown) => {
  return (error as { response?: { status?: number } })?.response?.status === 404;
};

export default class AiListingApi {
  static async createProductDraftFromAiProductDraft(
    params: CreateProductDraftFromAiParams,
  ): Promise<CreateProductDraftFromAiResponse> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.post<CreateProductDraftFromAiResponse>(
      `${BASE_URL}/deposit/ai-product-listing/product-drafts`,
      params,
      axiosConfig,
    );
    return data;
  }

  static async getDraftDetails(
    draftId: number,
    serverAxiosRequestData?: ServerAxiosRequestData,
  ): Promise<ProductDraftWithFormResponse> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    const { data } = await axios.get<ProductDraftWithFormResponse>(
      `${BASE_URL}/product-listing/product-drafts/${draftId}/details?order=AIListing`,
      axiosConfig,
    );
    return data;
  }

  static async updateDraft(draftId: number, fields: DraftFieldUpdates): Promise<UpdateDraftResponse> {
    const axiosConfig = getAxiosConfig();
    const formData = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      if (value === undefined) {
        continue;
      }
      const formKey = FIELD_KEY_MAP[key] ?? key;
      if (value instanceof File) {
        // Send the bill/invoice as binary rather than its stringified name.
        formData.append(formKey, value);
      } else if (Array.isArray(value)) {
        for (const v of value) {
          formData.append(`${formKey}[]`, String(v));
        }
      } else {
        formData.append(formKey, value === null ? '' : String(value));
      }
    }
    const { data } = await axios.post<UpdateDraftResponse>(
      `${BASE_URL}/product-listing/product-drafts/${draftId}`,
      formData,
      {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return data;
  }

  static async submitDraft(draftId: number): Promise<SubmitDraftResponse> {
    const axiosConfig = getAxiosConfig();
    const requestBody = new FormData();
    requestBody.set('withAddressV2', '1');
    const { data } = await axios.put<ApiResponse<SubmitDraftResponse>>(
      `${BASE_URL}/deposit/products/drafts/${draftId}/submit`,
      requestBody,
      {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    if (!data?.data) {
      throw new Error(`submitDraft: missing data in response for draft ${draftId}`);
    }

    return data.data;
  }

  static async getCategories(serverAxiosRequestData?: ServerAxiosRequestData): Promise<Universe[]> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    const { data } = await axios.get<ApiResponse<Universe[]>>(`${BASE_URL}/deposit/catalog`, axiosConfig);
    return data?.data ?? [];
  }

  static async getBrands(serverAxiosRequestData?: ServerAxiosRequestData): Promise<Brand[]> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    const { data } = await axios.get<ApiResponse<Brand[]>>(`${BASE_URL}/deposit/brands?active=1`, axiosConfig);
    return data?.data ?? [];
  }

  static async getShippingAddress(): Promise<AddressDetails | null> {
    const axiosConfig = getAxiosConfig();

    try {
      const { data } = await axios.get<ApiResponse<AddressDetails>>(
        `${BASE_URL}/users/addressV2/flag/${SHIPPING_ADDRESS_FLAG}`,
        axiosConfig,
      );

      return data.data ?? null;
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }

      throw error;
    }
  }

  static async getAddressById(addressId: number): Promise<AddressDetails | null> {
    const axiosConfig = getAxiosConfig();

    try {
      const { data } = await axios.get<ApiResponse<AddressDetails>>(
        `${BASE_URL}/users/addressV2/${addressId}`,
        axiosConfig,
      );

      return data.data ?? null;
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }

      throw error;
    }
  }

  static async getPriceDetails(pvp: string, draftId: number): Promise<PriceDetailsResponse> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.get<{ data: PriceDetailsResponse }>(`${BASE_URL}/price-details`, {
      ...axiosConfig,
      params: {
        ...axiosConfig.params,
        pvp,
        id_preduct: draftId,
      },
    });
    return data.data;
  }

  static async getPriceRecommendation(draftId: number): Promise<RecommendationsPriceResponse> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.get<{ data: RecommendationsPriceResponse }>(
      `${BASE_URL}/deposit/price/recommended/${draftId}`,
      axiosConfig,
    );
    return data.data;
  }

  static async getPriceZone(draftId: number, params: PriceZoneRequestParams): Promise<PriceZoneResponse> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.get<{ data: PriceZoneResponse }>(`${BASE_URL}/deposit/price/zone/${draftId}`, {
      ...axiosConfig,
      params: {
        ...axiosConfig.params,
        ...params,
      },
    });
    return data.data;
  }

  static async generateDescription(draftId: number): Promise<string | null> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.get<GenerateDescriptionResponse>(
      `${BASE_URL}/deposit/ai-product-listing/product-drafts/${draftId}/generate-description`,
      axiosConfig,
    );
    return data?.data?.description ?? null;
  }

  static async createAiProductDraft(
    serverAxiosRequestData?: ServerAxiosRequestData,
  ): Promise<CreateAiProductDraftResponse> {
    const axiosConfig = getAxiosConfig(serverAxiosRequestData);
    const { data } = await axios.post<CreateAiProductDraftResponse>(
      `${BASE_URL}/deposit/ai-product-listing/ai-product-drafts`,
      {},
      axiosConfig,
    );
    return data;
  }

  static async uploadPhotos(aiProductDraftId: string, files: File[], startIndex: number): Promise<PhotoUploadResponse> {
    const axiosConfig = getAxiosConfig();
    const formData = new FormData();

    files.forEach((file, i) => {
      formData.append(`photo${i}`, file);
      formData.append(`photo${i}Index`, String(startIndex + i));
      formData.append(`photo${i}LocalIdentifier`, crypto.randomUUID());
    });

    const { data } = await axios.post<PhotoUploadResponse>(
      `${BASE_URL}/deposit/ai-product-listing/ai-product-drafts/${aiProductDraftId}/photos`,
      formData,
      {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return data;
  }

  static async fetchPhotos(aiProductDraftId: string): Promise<PhotoUploadResult[]> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.get<PhotoUploadResponse>(
      `${BASE_URL}/deposit/ai-product-listing/ai-product-drafts/${aiProductDraftId}/photos`,
      axiosConfig,
    );
    return data.data.photos;
  }

  static async reorderPhotos(aiProductDraftId: string, photos: PhotoReorderItem[]): Promise<void> {
    const axiosConfig = getAxiosConfig();
    await axios.put(
      `${BASE_URL}/deposit/ai-product-listing/ai-product-drafts/${aiProductDraftId}/photos`,
      photos,
      axiosConfig,
    );
  }

  static async deletePhoto(aiProductDraftId: string, photoId: string): Promise<void> {
    const axiosConfig = getAxiosConfig();
    await axios.delete(
      `${BASE_URL}/deposit/ai-product-listing/ai-product-drafts/${aiProductDraftId}/photos/${photoId}`,
      axiosConfig,
    );
  }

  static async inferFields(aiProductDraftId: string): Promise<InferAiProductDraftResponse> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.post<InferAiProductDraftResponse>(
      `${BASE_URL}/deposit/ai-product-listing/ai-product-drafts/${aiProductDraftId}/analysis`,
      {},
      axiosConfig,
    );
    return data;
  }

  static async fetchProductDraftPhotos(draftId: number): Promise<FetchProductDraftPhotosResponse> {
    const axiosConfig = getAxiosConfig();
    const { data } = await axios.get<{ data: FetchProductDraftPhotosResponse }>(
      `${BASE_URL}/deposit/photos/products/drafts/${draftId}`,
      axiosConfig,
    );
    return data.data;
  }

  static async uploadProductDraftPhotos(
    draftId: number,
    files: File[],
    startIndex: number,
  ): Promise<AddProductDraftPhotosResponse> {
    const axiosConfig = getAxiosConfig();
    const formData = new FormData();
    formData.append('productDraftId', String(draftId));

    files.forEach((file, i) => {
      formData.append(`photo${i}`, file);
      formData.append(`photo${i}Index`, String(startIndex + i));
    });

    const { data } = await axios.post<{ data: AddProductDraftPhotosResponse }>(`${BASE_URL}/deposit/photos`, formData, {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return data.data;
  }

  static async reorderProductDraftPhotos(draftId: number, photos: PhotoReorderItem[]): Promise<void> {
    const axiosConfig = getAxiosConfig();
    await axios.put(`${BASE_URL}/deposit/photos/products/drafts/${draftId}`, photos, axiosConfig);
  }

  static async deleteProductDraftPhoto(draftId: number, photoId: string): Promise<void> {
    const axiosConfig = getAxiosConfig();
    await axios.delete(`${BASE_URL}/deposit/products/drafts/${draftId}/photos/${photoId}`, axiosConfig);
  }
}
